import Icon from "@mui/material/Icon";
import { addSpacesToTitleCase } from "common/helpers/utils";
import Guid from "common/values/guid/guid";
import { JSX } from 'react';

export enum AccessLevel {
  'Read-Only',
  'Read/Write',
}
export enum Scope {
  'Need-to-Know',
  'Entity-Wide',
}
export enum ResourceCategory {
  'Communication',
  'Financial',
  'Work',
  'Documents',
  'Marketplace',
  'Account',
  'KnowledgeBase'
}
export enum ResourceType {
  'GenericResource',
  'GeneralDocumentAccess',
  'DocumentContentsAccess',
  'DocumentAccess',
  'KnowledgeBaseAccess',
  'LegalEntitiesPlatformAccess',
  'EntityAgreementAccess',
  'LegalEntityAccess',
  'MarketplaceAccess',
  'CommunicationsAccess',
  'ForumAccess',
  'NotificationsAccess',
  'LoginAccess',
  'UserNetworkingAccess',
  'WorkAgreementsAccess',
  'ProposalsAccess',
  'TeamsAccess',
  'ContractsAccess',
  'FeeSchedulesAccess',
  'CurrencyFieldsAccess',
  'DiscountsAccess',
  'InvoicesAccess',
  'ProposalAccess',
  'WorkAgreementAccess'
}
export enum ChildResourceType {
  'DocumentAccess',
  'EntityAgreementAccess',
  'ForumAccess',
  'ProposalAccess',
  'WorkAgreementAccess'
}

export interface IGenericResource {
  rawText?: string;
  resourceString?: string;
}

// A constructor for any Resource subclass
export type ResourceConstructor<T extends Resource = Resource> = abstract new (...args: unknown[]) => T;

export abstract class Resource {
  public metaId: string = Guid.generate().toString();
  public isDisabled: boolean = false;
  public abstract readonly resourceType: ResourceType;
  public abstract get metaText(): string;
  public abstract rawText?: string;
  public abstract asJSXElement(...params: unknown[]): JSX.Element;
  public abstract toString(...params: unknown[]): string;
  public abstract fromObject(obj: unknown): Resource;
  public abstract toJSONString(): string;

  public get resourceTypeText(): string {
    return addSpacesToTitleCase(ResourceType[this.resourceType]);
  }

  public async clone(): Promise<Resource> {
    return await Resource.fromJSON(this.toJSONString());
  }

  public isEqualTo(other: Resource): boolean {
    return this.toJSONString() === other.toJSONString();
  }

  public static async fromJSON(json: string): Promise<Resource> {
    const obj = JSON.parse(json);
    if (!obj.resourceType) {
      throw new Error('resourceType is required');
    }
    // Map of resourceType to class constructor
    let resource: Resource;
    switch (obj.resourceType) {
      case 'GeneralDocumentAccess': {
        const { GeneralDocumentAccess } = await import('./documents-resources');
        resource = new GeneralDocumentAccess();
        break;
      }
      case 'DocumentContentsAccess': {
        const { DocumentContentsAccess } = await import('./documents-resources');
        resource = new DocumentContentsAccess();
        break;
      }
      case 'DocumentAccess': {
        const { DocumentAccess } = await import('./documents-resources');
        resource = new DocumentAccess();
        break;
      }
      case 'LegalEntitiesPlatformAccess': {
        const { LegalEntitiesPlatformAccess } = await import('./legal-entities-resources');
        resource = new LegalEntitiesPlatformAccess();
        break;
      }
      case 'EntityAgreementAccess': {
        const { EntityAgreementAccess } = await import('./legal-entities-resources');
        resource = new EntityAgreementAccess();
        break;
      }
      case 'LegalEntityAccess': {
        const { LegalEntityAccess } = await import('./legal-entities-resources');
        resource = new LegalEntityAccess();
        break;
      }
      case 'CommunicationsAccess': {
        const { CommunicationsAccess } = await import('./messaging-resources');
        resource = new CommunicationsAccess();
        break;
      }
      case 'ForumAccess': {
        const { ForumAccess } = await import('./messaging-resources');
        resource = new ForumAccess();
        break;
      }
      case 'MarketplaceAccess': {
        const { MarketplaceAccess } = await import('./marketplace-resources');
        resource = new MarketplaceAccess();
        break;
      }
      case 'NotificationsAccess': {
        const { NotificationsAccess } = await import('./notifications-resources');
        resource = new NotificationsAccess();
        break;
      }
      case 'LoginAccess': {
        const { LoginAccess } = await import('./users-resources');
        resource = new LoginAccess();
        break;
      }
      case 'UserNetworkingAccess': {
        const { UserNetworkingAccess } = await import('./users-resources');
        resource = new UserNetworkingAccess();
        break;
      }
      case 'WorkAgreementsAccess': {
        const { WorkAgreementsAccess } = await import('./work-resources');
        resource = new WorkAgreementsAccess();
        break;
      }
      case 'ProposalsAccess': {
        const { ProposalsAccess } = await import('./work-resources');
        resource = new ProposalsAccess();
        break;
      }
      case 'TeamsAccess': {
        const { TeamsAccess } = await import('./work-resources');
        resource = new TeamsAccess();
        break;
      }
      case 'ContractsAccess': {
        const { ContractsAccess } = await import('./work-resources');
        resource = new ContractsAccess();
        break;
      }
      case 'FeeSchedulesAccess': {
        const { FeeSchedulesAccess } = await import('./work-resources');
        resource = new FeeSchedulesAccess();
        break;
      }
      case 'CurrencyFieldsAccess': {
        const { CurrencyFieldsAccess } = await import('./work-resources');
        resource = new CurrencyFieldsAccess();
        break;
      }
      case 'DiscountsAccess': {
        const { DiscountsAccess } = await import('./work-resources');
        resource = new DiscountsAccess();
        break;
      }
      case 'InvoicesAccess': {
        const { InvoicesAccess } = await import('./work-resources');
        resource = new InvoicesAccess();
        break;
      }
      case 'ProposalAccess': {
        const { ProposalAccess } = await import('./work-resources');
        resource = new ProposalAccess();
        break;
      }
      case 'WorkAgreementAccess': {
        const { WorkAgreementAccess } = await import('./work-resources');
        resource = new WorkAgreementAccess();
        break;
      }
      case 'KnowledgeBaseAccess': {
        const { KnowledgeBaseAccess } = await import('./knowledge-base-resources');
        resource = new KnowledgeBaseAccess();
        break;
      }
      default:
        resource = new GenericResource();
    }

    return resource.fromObject(obj);
  }

  public static generatePermutations(
    resourceClass: new () => Resource,
    fieldIterations: [key: string, value: unknown[]][]
  ): Resource[] {
    const enumValuesArrays = fieldIterations.map(([_, values]) => values);
    const resourcePermutations: Resource[] = [];

    function permutate(arrays: unknown[][], prefix: unknown[] = []): unknown[][] {
      if (!arrays.length) return [prefix];
      const [first, ...rest] = arrays;
      return first.flatMap(value => permutate(rest, [...prefix, value]));
    }

    const permutations = permutate(enumValuesArrays);
    for (const permutation of permutations) {
      const resource = new resourceClass();
      for (let i = 0; i < fieldIterations.length; i++) {
        const [key] = fieldIterations[i];
        (resource as unknown as Record<string, unknown>)[key] = permutation[i];
      }
      resourcePermutations.push(resource);
    }
    return resourcePermutations;
  }
}

export class GenericResource extends Resource {
  public readonly resourceType: ResourceType = ResourceType.GenericResource;
  public resourceString?: string;
  public rawText?: string;

  public get metaText(): string {
    return this.resourceString || '';
  }

  public asJSXElement(): JSX.Element {
    return this.resourceString ? <span>{this.resourceString}</span> : <Icon>remove</Icon>;
  }

  public override toString(): string {
    return this.resourceString || '-';
  }

  public fromObject(obj: IGenericResource): Resource {
    const resource = new GenericResource();
    resource.rawText = obj.rawText || "";
    resource.resourceString = JSON.stringify(obj);
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      resourceString: this.resourceString ?? null,
      rawText: this.rawText ?? null,
    });
  }
}
